import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Award,
  Calendar,
  User,
  Clock,
  Printer,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Banknote,
  ArrowRightLeft,
  Package,
  Download
} from 'lucide-react';

import { useAuth, API } from '../../contexts/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { getErrorMessage } from '../../utils/errorHandler';

// Función para formatear montos con separadores de miles y 2 decimales
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return `$${Number(amount).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ReportsTab = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('daily');
  const [loading, setLoading] = useState(false);
  
  // Date filters
  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  const [startDate, setStartDate] = useState(weekAgo);
  const [endDate, setEndDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState(today);
  
  // Report data
  const [salesReport, setSalesReport] = useState(null);
  const [popularProducts, setPopularProducts] = useState([]);
  const [kitchenPerformance, setKitchenPerformance] = useState(null);
  const [cashierReport, setCashierReport] = useState(null);
  const [dailyReport, setDailyReport] = useState(null);
  const [shiftReport, setShiftReport] = useState(null);
  const [productsReport, setProductsReport] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  
  // Expanded sections
  const [expandedCashier, setExpandedCashier] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);

  const loadBasicReports = useCallback(async () => {
    try {
      const [salesRes, productsRes, kitchenRes] = await Promise.all([
        axios.get(`${API}/reports/sales`, { headers: getAuthHeaders() }),
        axios.get(`${API}/reports/popular-products`, { headers: getAuthHeaders() }),
        axios.get(`${API}/reports/kitchen-performance`, { headers: getAuthHeaders() })
      ]);
      setSalesReport(salesRes.data);
      setPopularProducts(productsRes.data);
      setKitchenPerformance(kitchenRes.data);
    } catch (error) {
      console.error('Error loading basic reports:', error);
    }
  }, [getAuthHeaders]);

  const loadCashierReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/sales-by-cashier?start_date=${startDate}&end_date=${endDate}`,
        { headers: getAuthHeaders() }
      );
      setCashierReport(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar reporte por cajero'));
    }
    setLoading(false);
  }, [getAuthHeaders, startDate, endDate]);

  const loadDailyReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/sales-by-day?start_date=${startDate}&end_date=${endDate}`,
        { headers: getAuthHeaders() }
      );
      setDailyReport(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar reporte diario'));
    }
    setLoading(false);
  }, [getAuthHeaders, startDate, endDate]);

  const loadShiftReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/sales-by-shift?date=${selectedDate}`,
        { headers: getAuthHeaders() }
      );
      setShiftReport(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar reporte por turno'));
    }
    setLoading(false);
  }, [getAuthHeaders, selectedDate]);

  // Reporte de catálogo: todos los productos con su categoría, precio, costo,
  // existencia y valor de inventario (costo × existencia). Los datos de costo/stock
  // vienen ya combinados en GET /products (merge de la colección inventory).
  const loadProductsReport = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categories`, { headers: getAuthHeaders() })
      ]);
      const prods = Array.isArray(productsRes.data) ? productsRes.data : [];
      const cats = Array.isArray(categoriesRes.data) ? categoriesRes.data : [];
      const catMap = new Map(cats.map((c) => [c.id, c.name]));
      setProductsReport(prods.map((p) => {
        const tracked = p.tracked === true;
        const cost = Number(p.cost) || 0;
        const stock = Number(p.stock) || 0;
        // Con control por variante, el valor se calcula por slot (Σ stock×costo de las
        // variantes gestionadas); si no, a nivel producto (costo×existencia).
        const variants = Array.isArray(p.variants) ? p.variants : [];
        const variantTracked = p.variant_tracked === true && variants.length > 0;
        const inventory_value = variantTracked
          ? variants
              .filter((v) => v && v.tracked !== false)
              .reduce((s, v) => s + (Number(v.stock) || 0) * (Number(v.cost) || 0), 0)
          : (tracked ? cost * stock : 0);
        return {
          id: p.id,
          name: p.name || '',
          category_name: catMap.get(p.category_id) || '',
          price: Number(p.price) || 0,
          cost,
          stock,
          tracked,
          variant_tracked: variantTracked,
          inventory_value
        };
      }));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar el reporte de productos'));
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    loadBasicReports();
  }, [loadBasicReports]);

  useEffect(() => {
    if (activeTab === 'cashier') loadCashierReport();
    else if (activeTab === 'daily') loadDailyReport();
    else if (activeTab === 'shift') loadShiftReport();
    else if (activeTab === 'products') loadProductsReport();
  }, [activeTab, loadCashierReport, loadDailyReport, loadShiftReport, loadProductsReport]);

  const printReport = (type) => {
    const printWindow = window.open('', '_blank');
    let content = '';
    
    if (type === 'cashier' && cashierReport) {
      content = generateCashierPrintContent();
    } else if (type === 'daily' && dailyReport) {
      content = generateDailyPrintContent();
    } else if (type === 'shift' && shiftReport) {
      content = generateShiftPrintContent();
    } else if (type === 'products') {
      content = generateProductsPrintContent();
    }
    
    printWindow.document.write(content);
    printWindow.document.close();
  };

  const generateCashierPrintContent = () => {
    const cashiersHTML = cashierReport.cashiers.map(c => `
      <tr>
        <td>${c.cashier_name}</td>
        <td>${formatCurrency(c.total_cash)}</td>
        <td>${formatCurrency(c.total_card)}</td>
        <td>${formatCurrency(c.total_transfer)}</td>
        <td><strong>${formatCurrency(c.total_sales)}</strong></td>
        <td class="${c.total_difference >= 0 ? 'positive' : 'negative'}">${formatCurrency(c.total_difference)}</td>
      </tr>
    `).join('');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reporte de Ventas por Cajero</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; }
          .period { text-align: center; color: #666; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: right; }
          th { background: #f5f5f5; }
          td:first-child, th:first-child { text-align: left; }
          .positive { color: green; }
          .negative { color: red; }
          .summary { margin-top: 20px; padding: 15px; background: #f9f9f9; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>📊 Reporte de Ventas por Cajero</h1>
        <p class="period">Período: ${cashierReport.start_date} al ${cashierReport.end_date}</p>
        
        <table>
          <thead>
            <tr>
              <th>Cajero</th>
              <th>Efectivo</th>
              <th>Tarjeta</th>
              <th>Transferencia</th>
              <th>Total Ventas</th>
              <th>Diferencia</th>
            </tr>
          </thead>
          <tbody>
            ${cashiersHTML}
          </tbody>
        </table>
        
        <div class="summary">
          <h3>Resumen General</h3>
          <p><strong>Total Cajeros:</strong> ${cashierReport.summary.total_cashiers}</p>
          <p><strong>Total Sesiones:</strong> ${cashierReport.summary.total_sessions}</p>
          <p><strong>Total Ventas:</strong> ${formatCurrency(cashierReport.summary.grand_total_sales)}</p>
          <p><strong>Diferencia Total:</strong> ${formatCurrency(cashierReport.summary.grand_total_difference)}</p>
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
  };

  const generateDailyPrintContent = () => {
    const daysHTML = dailyReport.days.map(d => `
      <tr>
        <td>${d.date}</td>
        <td>${d.orders_count}</td>
        <td>${formatCurrency(d.cash_sales)}</td>
        <td>${formatCurrency(d.card_sales)}</td>
        <td>${formatCurrency(d.transfer_sales)}</td>
        <td><strong>${formatCurrency(d.total_sales)}</strong></td>
        <td>${formatCurrency(d.average_ticket)}</td>
      </tr>
    `).join('');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reporte de Ventas Diario</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; }
          .period { text-align: center; color: #666; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: right; }
          th { background: #f5f5f5; }
          td:first-child, th:first-child { text-align: left; }
          .summary { margin-top: 20px; padding: 15px; background: #f9f9f9; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>📅 Reporte de Ventas Diario</h1>
        <p class="period">Período: ${dailyReport.start_date} al ${dailyReport.end_date}</p>
        
        <table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Órdenes</th>
              <th>Efectivo</th>
              <th>Tarjeta</th>
              <th>Transferencia</th>
              <th>Total</th>
              <th>Ticket Prom.</th>
            </tr>
          </thead>
          <tbody>
            ${daysHTML}
          </tbody>
        </table>
        
        <div class="summary">
          <h3>Resumen del Período</h3>
          <p><strong>Total Órdenes:</strong> ${dailyReport.summary.total_orders}</p>
          <p><strong>Total Ventas:</strong> ${formatCurrency(dailyReport.summary.total_sales)}</p>
          <p><strong>Promedio Diario:</strong> ${formatCurrency(dailyReport.summary.average_daily_sales)}</p>
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
  };

  const generateShiftPrintContent = () => {
    const shiftsHTML = shiftReport.shifts.map(s => `
      <tr>
        <td>Turno ${s.shift_number}</td>
        <td>${s.cashier_name}</td>
        <td>${s.opening_time?.slice(11, 16) || '-'} - ${s.closing_time?.slice(11, 16) || 'Abierto'}</td>
        <td>${s.orders_count}</td>
        <td>${formatCurrency(s.orders_cash)}</td>
        <td>${formatCurrency(s.orders_card)}</td>
        <td>${formatCurrency(s.orders_transfer)}</td>
        <td><strong>${formatCurrency(s.orders_total)}</strong></td>
        <td class="${s.difference >= 0 ? 'positive' : 'negative'}">${formatCurrency(s.difference)}</td>
      </tr>
    `).join('');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reporte de Ventas por Turno</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; }
          .date { text-align: center; color: #666; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: right; }
          th { background: #f5f5f5; }
          td:first-child, td:nth-child(2), td:nth-child(3), th:first-child, th:nth-child(2), th:nth-child(3) { text-align: left; }
          .positive { color: green; }
          .negative { color: red; }
          .summary { margin-top: 20px; padding: 15px; background: #f9f9f9; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>⏰ Reporte de Ventas por Turno</h1>
        <p class="date">Fecha: ${shiftReport.date}</p>
        
        <table>
          <thead>
            <tr>
              <th>Turno</th>
              <th>Cajero</th>
              <th>Horario</th>
              <th>Órdenes</th>
              <th>Efectivo</th>
              <th>Tarjeta</th>
              <th>Transfer.</th>
              <th>Total</th>
              <th>Diferencia</th>
            </tr>
          </thead>
          <tbody>
            ${shiftsHTML}
          </tbody>
        </table>
        
        <div class="summary">
          <h3>Resumen del Día</h3>
          <p><strong>Total Turnos:</strong> ${shiftReport.summary.total_shifts}</p>
          <p><strong>Total Órdenes:</strong> ${shiftReport.summary.total_orders}</p>
          <p><strong>Total Ventas:</strong> ${formatCurrency(shiftReport.summary.total_sales)}</p>
          <p><strong>Diferencia Total:</strong> ${formatCurrency(shiftReport.summary.total_difference)}</p>
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
  };

  // Filtrado y totales del reporte de productos (se usa en pantalla y al imprimir)
  const filteredProductsReport = productsReport.filter((p) => {
    const q = productSearch.trim().toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || (p.category_name || '').toLowerCase().includes(q);
  });

  const productsTotals = {
    inventoryValue: productsReport.reduce((s, p) => s + (p.inventory_value || 0), 0),
    tracked: productsReport.filter((p) => p.tracked).length,
    outOfStock: productsReport.filter((p) => p.tracked && p.stock <= 0).length
  };

  const generateProductsPrintContent = () => {
    const rowsHTML = filteredProductsReport.map((p) => `
      <tr>
        <td>${p.name}</td>
        <td>${p.category_name || '—'}</td>
        <td>${formatCurrency(p.price)}</td>
        <td>${p.tracked && !p.variant_tracked ? formatCurrency(p.cost) : '—'}</td>
        <td>${p.tracked ? p.stock : 'Sin control'}</td>
        <td>${formatCurrency(p.inventory_value)}</td>
      </tr>
    `).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reporte de Productos</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; }
          .period { text-align: center; color: #666; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: right; }
          th { background: #f5f5f5; }
          td:first-child, td:nth-child(2), th:first-child, th:nth-child(2) { text-align: left; }
          .summary { margin-top: 20px; padding: 15px; background: #f9f9f9; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>📦 Reporte de Productos</h1>
        <p class="period">Generado: ${new Date().toLocaleString('es-MX')}</p>
        <table>
          <thead>
            <tr>
              <th>Producto</th>
              <th>Categoría</th>
              <th>Precio Venta</th>
              <th>Costo</th>
              <th>Existencia</th>
              <th>Valor Inventario</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHTML}
          </tbody>
        </table>
        <div class="summary">
          <h3>Resumen</h3>
          <p><strong>Total Productos:</strong> ${filteredProductsReport.length}</p>
          <p><strong>Con Inventario:</strong> ${productsTotals.tracked}</p>
          <p><strong>Agotados:</strong> ${productsTotals.outOfStock}</p>
          <p><strong>Valor Total del Inventario:</strong> ${formatCurrency(productsTotals.inventoryValue)}</p>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
  };

  // Exporta a CSV el reporte de productos (respeta el filtro de búsqueda actual).
  // Formato RFC 4180 (separador coma, decimales con punto) con BOM UTF-8 para Excel.
  const exportProductsCsv = () => {
    if (filteredProductsReport.length === 0) {
      toast.warning('No hay productos para exportar');
      return;
    }
    const esc = (val) => {
      const s = String(val ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const money = (n) => (Number(n) || 0).toFixed(2);
    const filteredInventoryValue = filteredProductsReport.reduce((s, p) => s + (p.inventory_value || 0), 0);
    const lines = [['Producto', 'Categoría', 'Precio Venta', 'Costo', 'Existencia', 'Valor Inventario'].map(esc).join(',')];
    filteredProductsReport.forEach((p) => {
      lines.push([
        esc(p.name),
        esc(p.category_name || ''),
        money(p.price),
        p.tracked && !p.variant_tracked ? money(p.cost) : '',
        p.tracked ? String(p.stock) : '',
        money(p.inventory_value)
      ].join(','));
    });
    lines.push([esc(`TOTAL (${filteredProductsReport.length} productos)`), '', '', '', '', money(filteredInventoryValue)].join(','));
    const csv = '\uFEFF' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
    link.href = url;
    link.download = `reporte-productos-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Reporte de productos exportado a CSV');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Reportes y Analytics</h2>
        <Button 
          variant="outline" 
          onClick={loadBasicReports}
          className="rounded-xl"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Quick Stats */}
      {salesReport && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Ventas Totales</p>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(salesReport.total_sales)}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Órdenes</p>
                <p className="text-2xl font-bold text-blue-700">
                  {salesReport.total_orders}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-white" />
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Ticket Promedio</p>
                <p className="text-2xl font-bold text-orange-700">
                  {formatCurrency(salesReport.average_order_value)}
                </p>
              </div>
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Report Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        <Button
          variant={activeTab === 'daily' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('daily')}
          className={activeTab === 'daily' ? 'bg-orange-500 hover:bg-orange-600' : ''}
        >
          <Calendar className="w-4 h-4 mr-2" />
          Por Día
        </Button>
        <Button
          variant={activeTab === 'cashier' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('cashier')}
          className={activeTab === 'cashier' ? 'bg-orange-500 hover:bg-orange-600' : ''}
        >
          <User className="w-4 h-4 mr-2" />
          Por Cajero
        </Button>
        <Button
          variant={activeTab === 'shift' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('shift')}
          className={activeTab === 'shift' ? 'bg-orange-500 hover:bg-orange-600' : ''}
        >
          <Clock className="w-4 h-4 mr-2" />
          Por Turno
        </Button>
        <Button
          variant={activeTab === 'products' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('products')}
          className={activeTab === 'products' ? 'bg-orange-500 hover:bg-orange-600' : ''}
        >
          <Package className="w-4 h-4 mr-2" />
          Productos
        </Button>
      </div>

      {/* Barra de filtros del reporte de productos */}
      {activeTab === 'products' && (
        <Card className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[220px]">
              <Label>Buscar producto</Label>
              <Input
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Nombre o categoría..."
                className="w-full"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => loadProductsReport()}
              disabled={loading}
              className="rounded-xl"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {loading ? 'Cargando...' : 'Actualizar'}
            </Button>
            <Button
              variant="outline"
              onClick={exportProductsCsv}
              className="ml-auto"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => printReport('products')}
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </div>
        </Card>
      )}

      {/* Filters */}
      {activeTab !== 'products' && (
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-4">
          {activeTab !== 'shift' ? (
            <>
              <div>
                <Label>Fecha Inicio</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div>
                <Label>Fecha Fin</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                />
              </div>
            </>
          ) : (
            <div>
              <Label>Fecha</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
              />
            </div>
          )}
          <Button 
            onClick={() => {
              if (activeTab === 'cashier') loadCashierReport();
              else if (activeTab === 'daily') loadDailyReport();
              else if (activeTab === 'shift') loadShiftReport();
            }}
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {loading ? 'Cargando...' : 'Aplicar Filtros'}
          </Button>
          <Button 
            variant="outline"
            onClick={() => printReport(activeTab)}
            className="ml-auto"
          >
            <Printer className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
        </div>
      </Card>
      )}

      {/* Daily Report */}
      {activeTab === 'daily' && dailyReport && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Total Ventas</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(dailyReport.summary.total_sales)}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Total Órdenes</p>
              <p className="text-2xl font-bold text-blue-600">{dailyReport.summary.total_orders}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Promedio Diario</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(dailyReport.summary.average_daily_sales)}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Órdenes/Día</p>
              <p className="text-2xl font-bold text-purple-600">{dailyReport.summary.average_daily_orders.toFixed(1)}</p>
            </Card>
          </div>

          {/* Daily Table */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Fecha</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Órdenes</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">
                      <div className="flex items-center justify-end gap-1">
                        <Banknote className="w-4 h-4" /> Efectivo
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">
                      <div className="flex items-center justify-end gap-1">
                        <CreditCard className="w-4 h-4" /> Tarjeta
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">
                      <div className="flex items-center justify-end gap-1">
                        <ArrowRightLeft className="w-4 h-4" /> Transfer.
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Total</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Ticket Prom.</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dailyReport.days.map((day) => (
                    <React.Fragment key={day.date}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedDay(expandedDay === day.date ? null : day.date)}>
                        <td className="px-4 py-3 font-medium">{day.date}</td>
                        <td className="px-4 py-3 text-right">{day.orders_count}</td>
                        <td className="px-4 py-3 text-right text-green-600">{formatCurrency(day.cash_sales)}</td>
                        <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(day.card_sales)}</td>
                        <td className="px-4 py-3 text-right text-purple-600">{formatCurrency(day.transfer_sales)}</td>
                        <td className="px-4 py-3 text-right font-bold">{formatCurrency(day.total_sales)}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(day.average_ticket)}</td>
                        <td className="px-4 py-3 text-center">
                          {expandedDay === day.date ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </td>
                      </tr>
                      {expandedDay === day.date && (
                        <tr>
                          <td colSpan={8} className="px-4 py-3 bg-gray-50">
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="text-gray-500">Por Tipo:</p>
                                <p>🍽️ En Local: {day.orders_by_type.dine_in}</p>
                                <p>🥡 Para Llevar: {day.orders_by_type.takeout}</p>
                                <p>🛵 Delivery: {day.orders_by_type.delivery}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Por Estado:</p>
                                {Object.entries(day.orders_by_status).map(([status, count]) => (
                                  <p key={status}>{status}: {count}</p>
                                ))}
                              </div>
                              <div>
                                <p className="text-gray-500">Turnos: {day.sessions_count}</p>
                                <p className="text-gray-500">Cobros: {day.payments_count}</p>
                                <p className={day.total_difference >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  Diferencia: {formatCurrency(day.total_difference)}
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="px-4 py-3 bg-gray-50 text-xs text-gray-500 border-t">
              El dinero se toma de los cobros registrados del día (incluye cada tramo de un pago dividido).
              La columna Órdenes cuenta las generadas en el día, estén o no cobradas.
            </p>
          </Card>
        </div>
      )}

      {/* Cashier Report */}
      {activeTab === 'cashier' && cashierReport && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Total Cajeros</p>
              <p className="text-2xl font-bold text-blue-600">{cashierReport.summary.total_cashiers}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Total Sesiones</p>
              <p className="text-2xl font-bold text-purple-600">{cashierReport.summary.total_sessions}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Total Ventas</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(cashierReport.summary.grand_total_sales)}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Diferencia Total</p>
              <p className={`text-2xl font-bold ${cashierReport.summary.grand_total_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(cashierReport.summary.grand_total_difference)}
              </p>
            </Card>
          </div>

          {/* Cashiers List */}
          <div className="space-y-3">
            {cashierReport.cashiers.map((cashier) => (
              <Card key={cashier.cashier_id} className="overflow-hidden">
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedCashier(expandedCashier === cashier.cashier_id ? null : cashier.cashier_id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold">{cashier.cashier_name}</p>
                      <p className="text-sm text-gray-500">{cashier.sessions.length} turno(s)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Total Ventas</p>
                      <p className="font-bold text-lg text-green-600">{formatCurrency(cashier.total_sales)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Diferencia</p>
                      <p className={`font-bold ${cashier.total_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(cashier.total_difference)}
                      </p>
                    </div>
                    {expandedCashier === cashier.cashier_id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>
                
                {expandedCashier === cashier.cashier_id && (
                  <div className="px-4 pb-4 border-t">
                    <div className="grid grid-cols-3 gap-4 my-3 text-sm">
                      <div className="text-center p-2 bg-green-50 rounded-lg">
                        <Banknote className="w-4 h-4 mx-auto text-green-600 mb-1" />
                        <p className="text-gray-500">Efectivo</p>
                        <p className="font-bold text-green-600">{formatCurrency(cashier.total_cash)}</p>
                      </div>
                      <div className="text-center p-2 bg-blue-50 rounded-lg">
                        <CreditCard className="w-4 h-4 mx-auto text-blue-600 mb-1" />
                        <p className="text-gray-500">Tarjeta</p>
                        <p className="font-bold text-blue-600">{formatCurrency(cashier.total_card)}</p>
                      </div>
                      <div className="text-center p-2 bg-purple-50 rounded-lg">
                        <ArrowRightLeft className="w-4 h-4 mx-auto text-purple-600 mb-1" />
                        <p className="text-gray-500">Transferencia</p>
                        <p className="font-bold text-purple-600">{formatCurrency(cashier.total_transfer)}</p>
                      </div>
                    </div>
                    
                    <p className="text-sm font-semibold mb-2">Detalle de Sesiones:</p>
                    <div className="space-y-2">
                      {cashier.sessions.map((session, idx) => (
                        <div key={session.session_id || idx} className="text-sm p-2 bg-gray-50 rounded-lg flex justify-between">
                          <div>
                            <p className="text-gray-600">
                              {session.opening_time?.slice(0, 10)} | {session.opening_time?.slice(11, 16)} - {session.closing_time?.slice(11, 16) || 'Abierta'}
                            </p>
                            <Badge variant={session.status === 'open' ? 'default' : 'secondary'}>
                              {session.status === 'open' ? 'Abierta' : 'Cerrada'}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{formatCurrency(session.system_total || 0)}</p>
                            <p className={`text-xs ${(session.difference || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              Dif: {formatCurrency(session.difference || 0)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Shift Report */}
      {activeTab === 'shift' && shiftReport && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Total Turnos</p>
              <p className="text-2xl font-bold text-blue-600">{shiftReport.summary.total_shifts}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Total Órdenes</p>
              <p className="text-2xl font-bold text-purple-600">{shiftReport.summary.total_orders}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Total Ventas</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(shiftReport.summary.total_sales)}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Diferencia Total</p>
              <p className={`text-2xl font-bold ${shiftReport.summary.total_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(shiftReport.summary.total_difference)}
              </p>
            </Card>
          </div>

          {/* Shifts Table */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Turno</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Cajero</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Horario</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Estado</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Órdenes</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Efectivo</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Tarjeta</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Transfer.</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Total</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Diferencia</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shiftReport.shifts.map((shift) => (
                    <tr key={shift.session_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-orange-500" />
                          <span className="font-medium">Turno {shift.shift_number}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{shift.cashier_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {shift.opening_time?.slice(11, 16)} - {shift.closing_time?.slice(11, 16) || 'Abierto'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={shift.status === 'open' ? 'default' : 'secondary'}>
                          {shift.status === 'open' ? 'Abierta' : 'Cerrada'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">{shift.orders_count}</td>
                      <td className="px-4 py-3 text-right text-green-600">{formatCurrency(shift.orders_cash)}</td>
                      <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(shift.orders_card)}</td>
                      <td className="px-4 py-3 text-right text-purple-600">{formatCurrency(shift.orders_transfer)}</td>
                      <td className="px-4 py-3 text-right font-bold">{formatCurrency(shift.orders_total)}</td>
                      <td className={`px-4 py-3 text-right font-bold ${shift.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(shift.difference)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="px-4 py-3 bg-gray-50 text-xs text-gray-500 border-t">
              Ventas del turno = cobros registrados entre la apertura y el cierre de la caja, con el método real de cada pago.
            </p>
          </Card>

          {/* Best Shift */}
          {shiftReport.summary.best_shift && (
            <Card className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
              <div className="flex items-center gap-3">
                <Award className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-sm text-gray-600">Mejor Turno del Día</p>
                  <p className="font-bold">
                    Turno {shiftReport.summary.best_shift.shift_number} - {shiftReport.summary.best_shift.cashier_name}
                  </p>
                  <p className="text-green-600 font-bold">{formatCurrency(shiftReport.summary.best_shift.orders_total)} en ventas</p>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Products Report */}
      {activeTab === 'products' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Total Productos</p>
              <p className="text-2xl font-bold text-blue-600">{productsReport.length}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Valor del Inventario</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(productsTotals.inventoryValue)}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Con Inventario</p>
              <p className="text-2xl font-bold text-purple-600">{productsTotals.tracked}</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-sm text-gray-500">Agotados</p>
              <p className="text-2xl font-bold text-red-600">{productsTotals.outOfStock}</p>
            </Card>
          </div>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Producto</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Categoría</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Precio Venta</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Costo</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Existencia</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Valor Inventario</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredProductsReport.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">{p.name}</td>
                      <td className="px-4 py-3 text-gray-600">{p.category_name || '—'}</td>
                      <td className="px-4 py-3 text-right text-orange-600">{formatCurrency(p.price)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{p.tracked && !p.variant_tracked ? formatCurrency(p.cost) : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        {p.tracked ? (
                          <span className={`font-semibold ${p.stock <= 0 ? 'text-red-600' : 'text-gray-800'}`}>
                            {p.stock}{p.stock <= 0 ? ' (agotado)' : ''}
                          </span>
                        ) : (
                          <span className="text-gray-400">Sin control</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-green-600">{formatCurrency(p.inventory_value)}</td>
                    </tr>
                  ))}
                  {filteredProductsReport.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        {productsReport.length === 0 ? 'No hay productos registrados' : 'Ningún producto coincide con la búsqueda'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="px-4 py-3 bg-gray-50 text-xs text-gray-500 border-t">
              Valor de inventario = costo × existencia. Los productos sin control de inventario no suman valor.
            </p>
          </Card>
        </div>
      )}

      {/* Kitchen Performance */}
      {kitchenPerformance && (
        <Card className="p-6 bg-white/80 backdrop-blur-xl border-orange-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">🍳 Rendimiento de Cocina (Hoy)</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-3 bg-yellow-50 rounded-xl">
              <p className="text-2xl font-bold text-yellow-600">{kitchenPerformance.pending}</p>
              <p className="text-sm text-gray-600">Pendientes</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-xl">
              <p className="text-2xl font-bold text-blue-600">{kitchenPerformance.in_preparation}</p>
              <p className="text-sm text-gray-600">En Prep.</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-xl">
              <p className="text-2xl font-bold text-green-600">{kitchenPerformance.ready}</p>
              <p className="text-sm text-gray-600">Listos</p>
            </div>
            <div className="text-center p-3 bg-indigo-50 rounded-xl">
              <p className="text-2xl font-bold text-indigo-600">{kitchenPerformance.delivered}</p>
              <p className="text-sm text-gray-600">Entregados</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-xl">
              <p className="text-2xl font-bold text-gray-800">{kitchenPerformance.total_today}</p>
              <p className="text-sm text-gray-600">Total Hoy</p>
            </div>
          </div>
        </Card>
      )}

      {/* Popular Products */}
      <Card className="p-6 bg-white/80 backdrop-blur-xl border-orange-100">
        <h3 className="text-lg font-bold text-gray-800 mb-4">🏆 Productos Más Vendidos</h3>
        {popularProducts.length > 0 ? (
          <div className="space-y-3">
            {popularProducts.slice(0, 5).map((product, index) => (
              <div key={product.product_id} className="flex items-center justify-between p-3 bg-orange-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">#{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{product.product_name}</p>
                    <p className="text-sm text-gray-600">{product.total_quantity} unidades</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-orange-600">{formatCurrency(product.total_revenue)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Award className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No hay datos de productos vendidos</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReportsTab;
